import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { get } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { t } from 'ember-intl';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { not } from 'ember-truth-helpers';
import { TrackedArray } from 'tracked-built-ins';

import { SelectedAttributesIndicator } from 'umg-ember-backend/components/ui/selected-attributes-indicator';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { lookupTranslation } from 'umg-ember-backend/utils/intl';

import Field from '../field';
import styles from './styles.module.css';

const bem = getBem(styles);

export default class FormFieldsetComponent extends Component {
  // Services
  @service intl;
  @service permission;

  // Defaults
  @tracked isClosed = false;
  fields = new TrackedArray([]);

  // Hooks
  constructor() {
    super(...arguments);

    if (this.args.isClosed) {
      this.isClosed = this.args.isClosed;
    }
  }

  // Getter and setter
  get isFieldsetVisible() {
    return Boolean(this.fields.length) || this.args.isVisible;
  }

  get instruction() {
    const lookups = [
      `forms.${this.args.recordType}.fieldset_instruction.${this.args.titleKey}`,
      `forms.defaults.fieldset_instruction.${this.args.titleKey}`,
    ];

    return lookupTranslation.call(this, lookups, { silent: true });
  }

  get legend() {
    const lookups = [
      `forms.${this.args.recordType}.fieldsets.${this.args.titleKey}`,
      `forms.defaults.fieldsets.${this.args.titleKey}`,
    ];

    return lookupTranslation.call(this, lookups);
  }

  get hasErrors() {
    return this.fields.find((field) => {
      return get(this.args.model, `validations.attrs.${field}.isInvalid`);
    });
  }

  get showErrors() {
    // Show errors if formShowValidationErrors is set
    if (this.args.formShowValidationErrors && this.hasErrors) {
      return true;
    }

    // Show errors on existing records
    if (!this.args.model.isNew && this.hasErrors) {
      return true;
    }

    return false;
  }

  // Functions
  toggleFieldset = () => {
    this.isClosed = !this.isClosed;
  };

  registerField = (property) => {
    if (!this.fields.includes(property)) {
      this.fields.push(property);
    }
  };

  unregisterField = (property) => {
    const index = this.fields.indexOf(property);
    if (index !== -1) {
      this.fields.splice(index, 1);
    }
  };

  // Template
  <template>
    <fieldset
      class={{bem
        (hash
          is-closed=this.isClosed
          is-hidden=(not this.isFieldsetVisible)
          has-error=this.showErrors
        )
      }}
      data-selector-ui-form-fieldset
      ...attributes
    >
      <div
        class={{bem 'legend'}}
        role='button'
        data-selector-ui-form-fieldset-legend
        {{on 'click' this.toggleFieldset}}
      >
        <div class={{bem 'legend-label'}}>
          {{this.legend}}
        </div>
        {{#if @showSelectedAttributesIndicator}}
          <SelectedAttributesIndicator
            @tooltipSide='left'
            @model={{@model}}
            @attributes={{this.fields}}
            @tooltip={{t
              'ui.form.fieldset.selected_attributes_indicator'
              legend=this.legend
            }}
          />
        {{/if}}
        {{svgJar 'chevron-down' class=(bem 'legend-icon')}}
      </div>

      <div class={{bem 'content'}} data-selector-ui-form-fieldset-content>
        {{#if this.instruction}}
          <div class={{bem 'instruction'}}>
            {{this.instruction}}
          </div>
        {{/if}}

        {{yield
          (hash
            field=(component
              Field
              formShowValidationErrors=@formShowValidationErrors
              model=@model
              recordType=@recordType
              hasPermissionToEdit=@hasPermissionToEdit
              registerField=this.registerField
              unregisterField=this.unregisterField
            )
            model=@model
            recordType=@recordType
          )
        }}
      </div>
    </fieldset>
  </template>
}
